/**
 * SED D1.1 — PURE severity bucketer for unresolved evidence conflicts.
 *
 * The deterministic Scoring Engine must NOT transitively load the Gemini
 * client. `evidenceConflict.ts:21` value-imports `geminiClient` (which loads
 * `@google/genai` at module-load), so a VALUE import of its
 * `countUnresolvedConflicts` would re-leak Gemini into the scorer. This module
 * therefore imports the conflict shape as a TYPE ONLY — under
 * `isolatedModules: true` a `import type` is fully erased at emit, so no value
 * binding (and no Gemini module graph) is pulled in. Precedent for this
 * Gemini-free pattern: `conflictGrouping.ts`, `conflictVariant.ts`,
 * `conflictResolver.ts` all `import type` from the same module and stay pure.
 *
 * The predicate `c => !c.userResolution && !c.autoResolved` is byte-identical to
 * the gen-time `countUnresolvedConflicts` (evidenceConflict.ts:858): a conflict
 * is UNRESOLVED unless the user picked a resolution OR the prefilter
 * auto-resolved it. Tested for predicate parity in
 * `__tests__/conflictSeverityPenalty.test.ts`.
 *
 * null / empty input → all-zero (absence of conflicts = no penalty).
 */
import type { EvidenceConflict, ConflictSeverity } from '../../services/evidenceConflict';

export interface UnresolvedConflictCountsBySeverity {
  total: number;
  bySeverity: Record<ConflictSeverity, number>;
}

/**
 * Bucket the UNRESOLVED slice of a conflict list by severity.
 *
 * "Unresolved" matches the gen-time predicate exactly: a conflict counts only
 * when the user has NOT picked a resolution AND the prefilter did NOT
 * auto-resolve it. (Prefilter auto-resolutions are also stamped
 * `userResolution`, but the explicit `!autoResolved` guard documents the
 * invariant and is robust to that field being set independently.)
 */
export function countUnresolvedConflictsBySeverity(
  conflicts: readonly EvidenceConflict[] | null | undefined,
): UnresolvedConflictCountsBySeverity {
  const bySeverity: Record<ConflictSeverity, number> = { low: 0, medium: 0, high: 0 };
  if (!conflicts || conflicts.length === 0) {
    return { total: 0, bySeverity };
  }
  const unresolved = conflicts.filter((c) => !c.userResolution && !c.autoResolved);
  for (const c of unresolved) {
    bySeverity[c.severity] = (bySeverity[c.severity] ?? 0) + 1;
  }
  return { total: unresolved.length, bySeverity };
}
